import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Info, Check, Timer, ChevronDown, Zap } from 'lucide-react';

import { api } from '../lib/api.js';
import { useApiData, useTimer } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { isoToday, mmss, minutesLabel } from '../lib/format.js';
import { Card, Button, Sheet, Field, NumberInput, TextArea, Choice, Spinner, ErrorNote, Pill } from '../components/ui.jsx';

// The session screen. Everything here is built around one finding from the
// brief: the hard part is getting into the gym, not the exercise itself.
// So — one tap to start, the short version offered up front and without
// apology, last time's weights pre-filled, and sets saved as they are ticked
// so nothing is lost if the session stops half way.

export default function Session() {
  const { week, slot } = useParams();
  const navigate = useNavigate();
  const { show } = useToast();

  const [short, setShort] = useState(false);
  const { data, loading, error, reload } = useApiData(`/training/session?week=${week}&slot=${slot}${short ? '&short=1' : ''}`, [week, slot, short]);

  const [sets, setSets] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [openCues, setOpenCues] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const timer = useTimer();

  useEffect(() => {
    if (!data) return;
    setSessionId(data.logged ? data.logged.id : null);
    const map = {};
    for (const s of data.loggedSets || []) map[`${s.exercise_id}:${s.set_index}`] = s;
    setSets(map);
  }, [data]);

  const allItems = useMemo(
    () => (data ? data.session.blocks.flatMap((b) => b.items.map((i) => ({ ...i, blockKind: b.kind }))) : []),
    [data],
  );
  const workingSets = useMemo(
    () => allItems.filter((i) => i.blockKind === 'strength').reduce((a, i) => a + (i.sets || 1), 0),
    [allItems],
  );
  const doneSets = Object.values(sets).filter((s) => s.done).length;

  if (loading && !data) return <Spinner label="Loading the session" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { session } = data;
  const alreadyDone = data.logged && ['done', 'shortened'].includes(data.logged.status);

  async function logSet(item, index, patch) {
    const key = `${item.exerciseId}:${index}`;
    const before = sets[key] || {};
    const next = { ...before, ...patch, exercise_id: item.exerciseId, set_index: index };
    setSets((s) => ({ ...s, [key]: next }));

    try {
      const res = await api.post('/training/sets', {
        sessionId,
        week: Number(week),
        slot: Number(slot),
        date: isoToday(),
        exerciseId: item.exerciseId,
        setIndex: index,
        reps: next.reps ?? null,
        weightKg: next.weight_kg ?? null,
        seconds: next.seconds ?? null,
        done: next.done !== false,
      });
      if (!sessionId) setSessionId(res.sessionId);
    } catch (err) {
      setSets((s) => ({ ...s, [key]: before }));
      show(err.message, 'crit');
    }
  }

  async function finish(payload) {
    try {
      await api.post('/training/session', {
        week: Number(week),
        slot: Number(slot),
        date: isoToday(),
        action: 'complete',
        short,
        ...payload,
      });
      show('Session logged. Well done.');
      navigate('/');
    } catch (err) {
      show(err.message, 'crit');
    }
  }

  return (
    <div className="min-h-screen bg-bg pb-40">
      <header className="sticky top-0 z-30 bg-bg/95 backdrop-blur border-b border-edge px-gutter py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-textSoft" aria-label="Close session">
            <X size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-body font-semibold truncate">{session.name}</p>
            <p className="text-meta text-textMute">
              Week {session.week} · {session.phase.name} · about {session.estimatedMinutes} min
            </p>
          </div>
          {workingSets > 0 && (
            <span className="text-meta text-textMute shrink-0">
              {doneSets}/{workingSets}
            </span>
          )}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-gutter pt-4">
        {alreadyDone && (
          <Card className="mb-4 border-good/40 bg-goodSoft">
            <p className="text-body-sm text-good font-semibold">Already logged as done.</p>
            <p className="text-meta text-textMute mt-1">You can still add or change any of the numbers below.</p>
          </Card>
        )}

        {session.deload && (
          <Card className="mb-4 bg-accentSoft border-accent/40">
            <Pill tone="accent">Easier week</Pill>
            <p className="text-body-sm text-textSoft mt-2">{session.note}</p>
          </Card>
        )}

        {!alreadyDone && (
          <button
            onClick={() => setShort(!short)}
            className={`w-full mb-4 p-3 rounded-control border text-left transition-colors ${
              short ? 'border-accent bg-accentSoft' : 'border-edge bg-panelHi'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Zap size={18} className={short ? 'text-accent' : 'text-textMute'} />
              <div className="flex-1">
                <p className="text-body-sm font-semibold">{short ? 'Short version — on' : 'Only got twenty minutes?'}</p>
                <p className="text-meta text-textMute">
                  {short ? 'The three lifts that matter, short warm-up, five-minute finisher.' : 'Tap for the cut-down version. It counts exactly the same.'}
                </p>
              </div>
            </div>
          </button>
        )}

        {session.blocks.map((block) => (
          <section key={block.kind} className="mb-5">
            <h2 className="section-title">{block.title}</h2>
            <div className="space-y-3">
              {block.items.map((item) => (
                <ExerciseCard
                  key={item.exerciseId}
                  item={item}
                  blockKind={block.kind}
                  sets={sets}
                  onLog={logSet}
                  onRest={(s) => timer.start(s)}
                  cuesOpen={openCues === item.exerciseId}
                  onToggleCues={() => setOpenCues(openCues === item.exerciseId ? null : item.exerciseId)}
                  previous={data.previousSets}
                />
              ))}
            </div>
          </section>
        ))}

        <Button className="w-full mb-3" onClick={() => setFinishing(true)}>
          <Check size={18} /> Finish session
        </Button>
        <Button
          variant="ghost"
          className="w-full mb-8"
          onClick={async () => {
            await api.post('/training/session', { week: Number(week), slot: Number(slot), date: isoToday(), action: 'skip' });
            show('Marked as not done. No harm in it.');
            navigate('/');
          }}
        >
          Not today
        </Button>
      </div>

      {timer.running && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-accent text-onAccent px-gutter py-3">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <Timer size={18} />
            <span className="text-body font-semibold flex-1">Rest — {mmss(timer.seconds)}</span>
            <button onClick={timer.stop} className="text-body-sm underline">
              Skip
            </button>
          </div>
        </div>
      )}

      <FinishSheet open={finishing} onClose={() => setFinishing(false)} onSubmit={finish} estimated={session.estimatedMinutes} />
    </div>
  );
}

function ExerciseCard({ item, blockKind, sets, onLog, onRest, cuesOpen, onToggleCues, previous }) {
  const ex = item.exercise;
  if (!ex) return null;

  const setCount = item.sets || 1;
  const isTimed = ex.unit === 'seconds';
  const tracksWeight = ex.load === 'weight';
  const lastPrev = (previous?.sets || []).filter((s) => s.exercise_id === item.exerciseId);

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-card-title font-display">{ex.name}</h3>
          <p className="text-meta text-textMute mt-0.5">
            {item.sets ? `${item.sets} × ` : ''}
            {isTimed ? minutesLabel(item.seconds || 30) : `${item.reps} reps`}
            {item.rest ? ` · ${item.rest}s rest` : ''}
            {item.note ? ` · ${item.note}` : ''}
          </p>
        </div>
        <button onClick={onToggleCues} className="p-1.5 -mr-1 text-textMute hover:text-accent shrink-0" aria-expanded={cuesOpen} aria-label="How to do it">
          {cuesOpen ? <ChevronDown size={18} /> : <Info size={18} />}
        </button>
      </div>

      {item.isNew && item.upgradeNote && (
        <p className="text-body-sm text-accent bg-accentSoft rounded-inner px-3 py-2 mt-3">{item.upgradeNote}</p>
      )}

      {cuesOpen && (
        <div className="mt-3 pt-3 border-t border-edge space-y-2">
          <ul className="space-y-1.5">
            {ex.cues.map((c, i) => (
              <li key={i} className="text-body-sm text-textSoft flex gap-2">
                <span className="text-accent shrink-0">·</span>
                {c}
              </li>
            ))}
          </ul>
          {ex.note && <p className="text-meta text-textMute">{ex.note}</p>}
          {ex.regression && (
            <p className="text-meta text-textMute">
              <strong className="text-textSoft">Too hard today?</strong> {ex.regression}
            </p>
          )}
          {ex.progression && (
            <p className="text-meta text-textMute">
              <strong className="text-textSoft">Ready for more?</strong> {ex.progression}
            </p>
          )}
        </div>
      )}

      {blockKind !== 'cooldown' && (
        <div className="mt-3 space-y-2">
          {Array.from({ length: setCount }, (_, i) => {
            const key = `${item.exerciseId}:${i}`;
            const logged = sets[key];
            const prev = lastPrev.find((s) => s.set_index === i);
            const done = !!logged?.done;
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="text-meta text-textMute w-10 shrink-0">Set {i + 1}</span>

                {tracksWeight && (
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    aria-label={`Weight for set ${i + 1}`}
                    className="field py-2 px-2.5 text-body-sm w-20"
                    placeholder={prev?.weight_kg ?? item.lastTime?.weight_kg ?? 'kg'}
                    value={logged?.weight_kg ?? ''}
                    onChange={(e) => onLog(item, i, { weight_kg: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                )}

                <input
                  type="number"
                  inputMode="numeric"
                  aria-label={isTimed ? `Seconds for set ${i + 1}` : `Reps for set ${i + 1}`}
                  className="field py-2 px-2.5 text-body-sm w-20"
                  placeholder={isTimed ? String(item.seconds || 30) : String(item.reps || 10)}
                  value={(isTimed ? logged?.seconds : logged?.reps) ?? ''}
                  onChange={(e) =>
                    onLog(item, i, isTimed
                      ? { seconds: e.target.value === '' ? null : Number(e.target.value) }
                      : { reps: e.target.value === '' ? null : Number(e.target.value) })
                  }
                />

                <button
                  onClick={() => {
                    const next = !done;
                    onLog(item, i, {
                      done: next,
                      reps: logged?.reps ?? (isTimed ? null : item.reps),
                      seconds: logged?.seconds ?? (isTimed ? item.seconds : null),
                      weight_kg: logged?.weight_kg ?? prev?.weight_kg ?? null,
                    });
                    if (next && item.rest) onRest(item.rest);
                  }}
                  aria-pressed={done}
                  aria-label={`Mark set ${i + 1} ${done ? 'not done' : 'done'}`}
                  className={`ml-auto w-11 h-11 rounded-control border flex items-center justify-center transition-colors ${
                    done ? 'bg-good border-good text-white' : 'border-edge text-textMute hover:border-edgeHi'
                  }`}
                >
                  <Check size={18} />
                </button>
              </div>
            );
          })}

          {item.lastTime && (
            <p className="text-meta text-textMute pt-1">
              Last time: {item.lastTime.weight_kg != null ? `${item.lastTime.weight_kg}kg × ` : ''}
              {item.lastTime.reps ?? '—'} reps
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function FinishSheet({ open, onClose, onSubmit, estimated }) {
  const [duration, setDuration] = useState('');
  const [felt, setFelt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <Sheet open={open} onClose={onClose} title="Session done">
      <Field label="How long did it take?" id="dur">
        <NumberInput id="dur" suffix="min" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder={String(estimated)} />
      </Field>

      <Field label="How did it feel?" id="felt">
        <Choice
          columns={2}
          value={felt}
          onChange={setFelt}
          options={[
            { value: 'easy', label: 'Easy' },
            { value: 'right', label: 'About right' },
            { value: 'hard', label: 'Hard work' },
            { value: 'rough', label: 'Rough' },
          ]}
        />
      </Field>

      <Field label="Anything to remember for next time?" id="note">
        <TextArea id="note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 14kg goblet squats felt easy — go up next week." />
      </Field>

      <Button
        className="w-full"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          await onSubmit({
            duration_min: duration ? Number(duration) : null,
            felt: felt || null,
            note: note || null,
            rpe: { easy: 4, right: 6, hard: 8, rough: 9 }[felt] ?? null,
          });
          setBusy(false);
        }}
      >
        Save and finish
      </Button>
    </Sheet>
  );
}
